import { useQuery as useZeroQuery } from "@rocicorp/zero/react";
import {
  appMutators,
  appQueries,
  type VehicleBookingCategory,
  type VehicleDamageSeverity,
  type VehicleDamageStatus,
  type VehicleDocumentType,
  type VehicleFuelType,
  type VehicleStatus,
  type VehicleTransmission,
  type VehicleType,
} from "@production-hub/shared";
import { generateUUID } from "@/lib/utils";
import { zeroErrorMessage } from "@/lib/zero-error";
import {
  runZeroMutation,
  useZeroClient,
  useZeroMutation,
} from "@/lib/zero-mutation";

export type {
  VehicleBookingCategory,
  VehicleDamageSeverity,
  VehicleDamageStatus,
  VehicleDocumentType,
  VehicleFuelType,
  VehicleStatus,
  VehicleTransmission,
  VehicleType,
};

type PersonRef = { id: string; firstName: string; lastName: string } | null;

/** Dates are Zero date numbers (UTC midnight ms), see isoDate/fromIsoDate */
export type VehicleImage = {
  id: string;
  vehicleID: string;
  imageURL: string;
  displayOrder: number;
};

export type VehicleDocument = {
  id: string;
  vehicleID: string;
  documentType: VehicleDocumentType;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt?: number | null;
  uploadedBy?: PersonRef;
};

export type VehicleServiceRecord = {
  id: string;
  vehicleID: string;
  serviceDate: number;
  mileageKm?: number | null;
  workshop?: string | null;
  description: string;
  cost?: number | null;
};

export type VehicleDamage = {
  id: string;
  vehicleID: string;
  title: string;
  description?: string | null;
  severity: VehicleDamageSeverity;
  status: VehicleDamageStatus;
  reportedOn: number;
  resolvedOn?: number | null;
  imageURL?: string | null;
  reportedBy?: PersonRef;
};

export type VehicleFields = {
  name: string;
  brand?: string | null;
  model?: string | null;
  vehicleType: VehicleType;
  status: VehicleStatus;
  licensePlate?: string | null;
  registrationNumber?: string | null;
  vin?: string | null;
  firstRegistration?: number | null;
  licenseCategory?: string | null;
  lengthM?: number | null;
  widthM?: number | null;
  heightM?: number | null;
  cargoLengthM?: number | null;
  cargoWidthM?: number | null;
  cargoHeightM?: number | null;
  emptyWeightKg?: number | null;
  grossWeightKg?: number | null;
  payloadKg?: number | null;
  towingCapacityKg?: number | null;
  seats?: number | null;
  fuelType?: VehicleFuelType | null;
  transmission?: VehicleTransmission | null;
  mileageKm?: number | null;
  hasTowHitch: boolean;
  hasPowerInverter: boolean;
  nextInspectionDate?: number | null;
  nextServiceDate?: number | null;
  nextServiceKm?: number | null;
  vignetteValidUntil?: number | null;
  insuranceValidUntil?: number | null;
  notes?: string | null;
};

export type Vehicle = VehicleFields & {
  id: string;
  updatedAt?: number | null;
  images?: VehicleImage[];
  documents?: VehicleDocument[];
  serviceRecords?: VehicleServiceRecord[];
  damages?: VehicleDamage[];
};

/** Vehicle fields as sent to the mutators: dates as "YYYY-MM-DD" strings */
export type VehicleInput = {
  [K in keyof VehicleFields]?: VehicleFields[K] extends
    number | null | undefined
    ? K extends `${string}Date` | `${string}Until` | "firstRegistration"
      ? string | null
      : VehicleFields[K]
    : VehicleFields[K];
};

/** Zero date number → "YYYY-MM-DD" */
export function isoDate(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return new Date(value).toISOString().slice(0, 10);
}

export const vehicleTypeLabels: Record<VehicleType, string> = {
  car: "Car",
  van: "Van",
  truck: "Truck",
  trailer: "Trailer",
  ob_van: "OB van",
  other: "Other",
};

export const vehicleStatusLabels: Record<VehicleStatus, string> = {
  active: "Active",
  in_service: "In service",
  out_of_service: "Out of service",
};

export const vehicleDocumentTypeLabels: Record<VehicleDocumentType, string> = {
  registration: "Vehicle registration",
  insurance: "Insurance",
  leasing: "Leasing",
  invoice: "Invoice",
  other: "Other",
};

function queryState(
  details: { type: string; error?: unknown },
  empty: boolean,
) {
  return {
    isLoading: details.type !== "complete" && empty,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useVehicles() {
  const [data, details] = useZeroQuery(appQueries.vehicles.list());
  const vehicles = (data ?? []) as unknown as Vehicle[];
  return { data: vehicles, ...queryState(details, vehicles.length === 0) };
}

export function useVehicle(id: string | undefined) {
  const [data, details] = useZeroQuery(
    appQueries.vehicles.byID({
      id: id ?? "00000000-0000-0000-0000-000000000000",
    }),
    { enabled: !!id },
  );
  const vehicle = (data ?? null) as unknown as Vehicle | null;
  return { data: vehicle, ...queryState(details, !vehicle) };
}

export function useCreateVehicle() {
  const zero = useZeroClient();
  return useZeroMutation<VehicleInput & { name: string }, { id: string }>({
    success: "Vehicle created",
    failure: "Failed to create vehicle",
    run: async (input) => {
      const id = generateUUID();
      await runZeroMutation(
        zero,
        appMutators.vehicles.create({ id, ...input } as never),
        "Vehicle create rejected",
      );
      return { id };
    },
  });
}

export function useUpdateVehicle() {
  const zero = useZeroClient();
  return useZeroMutation<VehicleInput & { id: string }, void>({
    success: "Vehicle saved",
    failure: "Failed to save vehicle",
    run: (input) =>
      runZeroMutation(
        zero,
        appMutators.vehicles.update(input as never),
        "Vehicle update rejected",
      ),
  });
}

export function useDeleteVehicle() {
  const zero = useZeroClient();
  return useZeroMutation<string, void>({
    success: "Vehicle deleted",
    failure: "Failed to delete vehicle",
    run: (id) =>
      runZeroMutation(
        zero,
        appMutators.vehicles.softDelete({ id }),
        "Vehicle delete rejected",
      ),
  });
}

/** Uploads a public file (photos) and returns its URL. */
async function uploadPublicFile(file: File) {
  const res = await fetch("/api/storage/upload", {
    method: "POST",
    headers: {
      "Content-Type": file.type,
      "X-File-Name-Encoded": encodeURIComponent(file.name),
    },
    body: file,
  });
  if (!res.ok) throw new Error("Failed to upload file");
  const { publicUrl } = (await res.json()) as { publicUrl: string };
  return publicUrl;
}

export function useUploadVehicleImage() {
  const zero = useZeroClient();
  return useZeroMutation<
    { vehicleId: string; file: File; displayOrder: number },
    void
  >({
    success: "Photo uploaded",
    failure: "Failed to upload photo",
    run: async ({ vehicleId, file, displayOrder }) => {
      const imageURL = await uploadPublicFile(file);
      await runZeroMutation(
        zero,
        appMutators.vehicleImages.create({
          id: generateUUID(),
          vehicleID: vehicleId,
          imageURL,
          displayOrder,
        }),
        "Vehicle photo create rejected",
      );
    },
  });
}

export function useDeleteVehicleImage() {
  const zero = useZeroClient();
  return useZeroMutation<VehicleImage, void>({
    success: "Photo deleted",
    failure: "Failed to delete photo",
    run: async (image) => {
      const key = image.imageURL.split("/").pop();
      if (key) {
        await fetch(`/api/storage/${encodeURIComponent(key)}`, {
          method: "DELETE",
        });
      }
      await runZeroMutation(
        zero,
        appMutators.vehicleImages.delete({ id: image.id }),
        "Vehicle photo delete rejected",
      );
    },
  });
}

/** URL of a private document; only works for signed-in employees. */
export function vehicleDocumentUrl(documentId: string, download = false) {
  return `/api/vehicle-documents/${documentId}/file${download ? "?download=1" : ""}`;
}

async function apiError(res: Response, fallback: string) {
  try {
    const body = (await res.json()) as { error?: string };
    return new Error(body.error || fallback);
  } catch {
    return new Error(fallback);
  }
}

export function useUploadVehicleDocument() {
  return useZeroMutation<
    { vehicleId: string; file: File; documentType: VehicleDocumentType },
    { id: string }
  >({
    success: "Document uploaded",
    failure: "Failed to upload document",
    run: async ({ vehicleId, file, documentType }) => {
      const res = await fetch(`/api/vehicle-documents/${vehicleId}`, {
        method: "POST",
        headers: {
          "Content-Type": file.type,
          "X-File-Name-Encoded": encodeURIComponent(file.name),
          "X-Document-Type": documentType,
        },
        body: file,
      });
      if (!res.ok) throw await apiError(res, "Upload failed");
      return (await res.json()) as { id: string };
    },
  });
}

export function useDeleteVehicleDocument() {
  return useZeroMutation<string, void>({
    success: "Document deleted",
    failure: "Failed to delete document",
    run: async (documentId) => {
      const res = await fetch(`/api/vehicle-documents/${documentId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw await apiError(res, "Delete failed");
    },
  });
}

export function useUpdateVehicleDocumentType() {
  const zero = useZeroClient();
  return useZeroMutation<
    { id: string; documentType: VehicleDocumentType },
    void
  >({
    success: "Document updated",
    failure: "Failed to update document",
    run: (input) =>
      runZeroMutation(
        zero,
        appMutators.vehicleDocuments.update(input),
        "Vehicle document update rejected",
      ),
  });
}

export type ServiceRecordInput = {
  serviceDate: string;
  mileageKm?: number | null;
  workshop?: string | null;
  description: string;
  cost?: number | null;
};

export function useSaveVehicleServiceRecord() {
  const zero = useZeroClient();
  return useZeroMutation<
    ServiceRecordInput & { id?: string; vehicleId: string },
    void
  >({
    success: "Service entry saved",
    failure: "Failed to save service entry",
    run: ({ id, vehicleId, ...fields }) =>
      runZeroMutation(
        zero,
        id
          ? appMutators.vehicleServiceRecords.update({ id, ...fields })
          : appMutators.vehicleServiceRecords.create({
              id: generateUUID(),
              vehicleID: vehicleId,
              ...fields,
            }),
        "Service entry rejected",
      ),
  });
}

export function useDeleteVehicleServiceRecord() {
  const zero = useZeroClient();
  return useZeroMutation<string, void>({
    success: "Service entry deleted",
    failure: "Failed to delete service entry",
    run: (id) =>
      runZeroMutation(
        zero,
        appMutators.vehicleServiceRecords.delete({ id }),
        "Service entry delete rejected",
      ),
  });
}

export type DamageInput = {
  title: string;
  description?: string | null;
  severity: VehicleDamageSeverity;
  reportedOn: string;
  imageURL?: string | null;
};

export function useSaveVehicleDamage() {
  const zero = useZeroClient();
  return useZeroMutation<
    DamageInput & {
      id?: string;
      vehicleId: string;
      reportedByPersonId?: string | null;
      imageFile?: File | null;
    },
    void
  >({
    success: "Damage saved",
    failure: "Failed to save damage",
    run: async ({
      id,
      vehicleId,
      reportedByPersonId,
      imageFile,
      ...fields
    }) => {
      const imageURL = imageFile
        ? await uploadPublicFile(imageFile)
        : fields.imageURL;
      await runZeroMutation(
        zero,
        id
          ? appMutators.vehicleDamages.update({ id, ...fields, imageURL })
          : appMutators.vehicleDamages.create({
              id: generateUUID(),
              vehicleID: vehicleId,
              reportedByPersonID: reportedByPersonId ?? null,
              ...fields,
              imageURL,
            }),
        "Damage rejected",
      );
    },
  });
}

export function useSetVehicleDamageStatus() {
  const zero = useZeroClient();
  return useZeroMutation<{ id: string; status: VehicleDamageStatus }, void>({
    success: "Damage updated",
    failure: "Failed to update damage",
    run: (input) =>
      runZeroMutation(
        zero,
        appMutators.vehicleDamages.update(input),
        "Damage update rejected",
      ),
  });
}

export function useDeleteVehicleDamage() {
  const zero = useZeroClient();
  return useZeroMutation<string, void>({
    success: "Damage deleted",
    failure: "Failed to delete damage",
    run: (id) =>
      runZeroMutation(
        zero,
        appMutators.vehicleDamages.delete({ id }),
        "Damage delete rejected",
      ),
  });
}

// --- Bookings ----------------------------------------------------------------

export type VehicleBooking = {
  id: string;
  vehicleID: string;
  category: VehicleBookingCategory;
  projectID?: string | null;
  title?: string | null;
  startDate: number;
  endDate: number;
  /** Project bookings: days of the "Driver <vehicle>" function */
  outboundDate?: number | null;
  returnDate?: number | null;
  /** Outbound trip driver (on the outbound date) */
  driverPersonID?: string | null;
  /** Return trip driver (on the return date) */
  returnDriverPersonID?: string | null;
  notes?: string | null;
  project?: {
    id: string;
    name: string;
    number?: string | null;
    eventStartDate?: number | null;
    deletedAt?: number | null;
    status?: string | null;
    types?: string[] | null;
  } | null;
  driver?: PersonRef;
  returnDriver?: PersonRef;
  vehicle?: Vehicle | null;
};

export const bookingCategoryLabels: Record<VehicleBookingCategory, string> = {
  project: "Project",
  service: "Service",
  tyre_change: "Tyre change",
  inspection: "MFK / inspection",
  cleaning: "Cleaning",
  other: "Other",
};

/** Bookings of soft-deleted projects are hidden everywhere */
function visibleBookings(data: unknown) {
  return ((data ?? []) as unknown as VehicleBooking[]).filter(
    (booking) => !booking.project?.deletedAt,
  );
}

export function useProjectVehicleBookings(projectId: string | undefined) {
  const [data, details] = useZeroQuery(
    appQueries.vehicleBookings.byProject({
      projectID: projectId ?? "00000000-0000-0000-0000-000000000000",
    }),
    { enabled: !!projectId },
  );
  const bookings = visibleBookings(data);
  return { data: bookings, ...queryState(details, bookings.length === 0) };
}

export function useVehicleBookings(vehicleId: string | undefined) {
  const [data, details] = useZeroQuery(
    appQueries.vehicleBookings.byVehicle({
      vehicleID: vehicleId ?? "00000000-0000-0000-0000-000000000000",
    }),
    { enabled: !!vehicleId },
  );
  const bookings = visibleBookings(data);
  return { data: bookings, ...queryState(details, bookings.length === 0) };
}

/** Bookings overlapping [from, to] (Zero date numbers, inclusive) */
export function useVehicleBookingsInRange(
  from: number,
  to: number,
  enabled = true,
) {
  const [data, details] = useZeroQuery(
    appQueries.vehicleBookings.inRange({ from, to }),
    { enabled },
  );
  const bookings = visibleBookings(data);
  return { data: bookings, ...queryState(details, bookings.length === 0) };
}

export type VehicleBookingInput = {
  id?: string;
  vehicleID: string;
  category: VehicleBookingCategory;
  projectID?: string | null;
  title?: string | null;
  /** "YYYY-MM-DD" */
  startDate: string;
  endDate: string;
  outboundDate?: string | null;
  returnDate?: string | null;
  driverPersonID?: string | null;
  returnDriverPersonID?: string | null;
  notes?: string | null;
};

export function useSaveVehicleBooking() {
  const zero = useZeroClient();
  return useZeroMutation<VehicleBookingInput, void>({
    success: "Booking saved",
    failure: "Failed to save booking",
    run: ({ id, ...fields }) =>
      runZeroMutation(
        zero,
        id
          ? appMutators.vehicleBookings.update({ id, ...fields })
          : appMutators.vehicleBookings.create({
              id: generateUUID(),
              ...fields,
            }),
        "Booking rejected",
      ),
  });
}

export function useDeleteVehicleBooking() {
  const zero = useZeroClient();
  return useZeroMutation<string, void>({
    success: "Booking deleted",
    failure: "Failed to delete booking",
    run: (id) =>
      runZeroMutation(
        zero,
        appMutators.vehicleBookings.delete({ id }),
        "Booking delete rejected",
      ),
  });
}
